/**
 * POS Sales - Sales/Checkout interface for Point of Sale
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingCart, Plus, Minus, Trash2, ArrowLeft, Search,
  CreditCard, Banknote, Smartphone, Check, X, Receipt,
  AlertCircle, Calculator
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { posAPI } from '../../services/api';
import { toast } from 'sonner';

// Payment modal
const PaymentModal = ({ cart, totals, onClose, onComplete }) => {
  const [method, setMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [processing, setProcessing] = useState(false);

  const change = parseFloat(amountReceived || 0) - totals.total;
  const canComplete = method !== 'cash' || change >= 0;

  const handleComplete = async () => {
    if (!canComplete) {
      toast.error('El monto recibido es insuficiente');
      return;
    }

    setProcessing(true);
    try {
      await onComplete({
        payment_method: method,
        amount_received: parseFloat(amountReceived) || totals.total,
        change_given: method === 'cash' ? Math.max(0, change) : 0,
        customer_name: customerName || null,
      });
    } finally {
      setProcessing(false);
    }
  };

  // Quick amounts for cash
  const quickAmounts = [50, 100, 200, 500].filter(a => a >= totals.total);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Cobrar Venta</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Total */}
          <div className="text-center py-4 bg-primary/10 rounded-xl">
            <p className="text-sm text-muted-foreground">Total a cobrar</p>
            <p className="text-4xl font-display font-bold text-primary">
              ${totals.total.toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {cart.length} artículo(s) • IVA incluido: ${totals.tax.toFixed(2)}
            </p>
          </div>

          {/* Payment Method */}
          <div>
            <p className="text-sm font-medium mb-2">Método de pago</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'cash', icon: Banknote, label: 'Efectivo' },
                { id: 'card', icon: CreditCard, label: 'Tarjeta' },
                { id: 'transfer', icon: Smartphone, label: 'Transf.' },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMethod(m.id)}
                  className={`p-3 rounded-xl border-2 transition-colors ${
                    method === m.id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <m.icon className={`w-5 h-5 mx-auto mb-1 ${method === m.id ? 'text-primary' : ''}`} />
                  <p className="text-xs font-medium">{m.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Cash Amount */}
          {method === 'cash' && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Monto recibido</p>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
                placeholder="0.00"
                className="text-xl text-center font-bold"
                autoFocus
              />
              
              {/* Quick amounts */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAmountReceived(totals.total.toFixed(2))}
                  className="flex-1"
                >
                  Exacto
                </Button>
                {quickAmounts.slice(0, 3).map((a) => (
                  <Button
                    key={a}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAmountReceived(a.toString())}
                    className="flex-1"
                  >
                    ${a}
                  </Button>
                ))}
              </div>

              {/* Change */}
              {parseFloat(amountReceived) > 0 && (
                <div className={`p-3 rounded-lg text-center ${
                  change >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'
                }`}>
                  <p className="text-sm">Cambio</p>
                  <p className={`text-2xl font-bold ${
                    change >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    ${Math.max(0, change).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Customer Name (optional) */}
          <div>
            <p className="text-sm font-medium mb-1">Nombre cliente (opcional)</p>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Para el ticket"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleComplete} 
              className="flex-1"
              disabled={processing || !canComplete}
            >
              {processing ? 'Procesando...' : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Completar
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Sale complete modal
const SaleCompleteModal = ({ sale, onClose, onNewSale }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/90 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-sm bg-card rounded-2xl border border-border shadow-xl text-center p-6"
      >
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        
        <h2 className="font-display font-bold text-xl mb-2">¡Venta Completada!</h2>
        <p className="text-muted-foreground mb-4">Ticket #{sale?.sale_number}</p>
        
        <div className="bg-secondary rounded-lg p-4 mb-4">
          <p className="text-3xl font-display font-bold">${sale?.total?.toFixed(2)}</p>
          {sale?.payment_method === 'cash' && sale?.change_given > 0 && (
            <p className="text-sm text-muted-foreground">
              Cambio: ${sale.change_given.toFixed(2)}
            </p>
          )}
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            <Receipt className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
          <Button onClick={onNewSale} className="flex-1">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Venta
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function POSSales() {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [hasOpenSession, setHasOpenSession] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [cart, setCart] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [completedSale, setCompletedSale] = useState(null);

  useEffect(() => {
    checkSessionAndLoad();
  }, []);

  const checkSessionAndLoad = async () => {
    try {
      setLoading(true);
      const [sessionRes, productsRes, deptsRes] = await Promise.all([
        posAPI.getCurrentSession(),
        posAPI.getProducts(),
        posAPI.getDepartments()
      ]);

      if (!sessionRes.data.has_open_session) {
        setHasOpenSession(false);
        return;
      }

      setHasOpenSession(true);
      setProducts(productsRes.data.products || []);
      setDepartments(deptsRes.data.departments || []);
    } catch (err) {
      console.error('Error loading sales:', err);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // Add product to cart
  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product_id === product.id);
      if (existing) {
        // Check stock
        if (existing.quantity >= product.stock) {
          toast.error('Stock insuficiente');
          return prev;
        }
        return prev.map((item) =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
            : item
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: product.sale_price,
          has_iva: product.has_iva,
          subtotal: product.sale_price,
          max_stock: product.stock,
        },
      ];
    });
  };

  // Update cart item quantity
  const updateQuantity = (productId, delta) => {
    setCart((prev) => {
      return prev.map((item) => {
        if (item.product_id === productId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          if (newQty > item.max_stock) {
            toast.error('Stock insuficiente');
            return item;
          }
          return { ...item, quantity: newQty, subtotal: newQty * item.unit_price };
        }
        return item;
      }).filter(Boolean);
    });
  };

  // Remove from cart
  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.product_id !== productId));
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    const tax = cart.reduce((sum, item) => {
      if (item.has_iva) {
        return sum + (item.subtotal * 0.16 / 1.16); // IVA is included in price
      }
      return sum;
    }, 0);
    return {
      subtotal: subtotal - tax,
      tax,
      total: subtotal,
    };
  };

  // Complete sale
  const handleCompleteSale = async (paymentData) => {
    const totals = calculateTotals();
    
    try {
      const saleData = {
        items: cart.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: 0,
          subtotal: item.subtotal,
        })),
        subtotal: totals.subtotal,
        discount: 0,
        tax: totals.tax,
        total: totals.total,
        ...paymentData,
      };

      const res = await posAPI.createSale(saleData);
      setCompletedSale(res.data.sale);
      setShowPayment(false);
      setCart([]);
      // Reload products to update stock
      const productsRes = await posAPI.getProducts();
      setProducts(productsRes.data.products || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando venta');
      throw err;
    }
  };

  // Filter products
  const filteredProducts = products.filter((p) => {
    if (!p.is_active || p.stock <= 0) return false;
    const matchesSearch = !searchTerm || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.barcode?.includes(searchTerm);
    const matchesDept = !selectedDept || p.department_id === selectedDept;
    return matchesSearch && matchesDept;
  });

  const totals = calculateTotals();

  // No session screen
  if (!loading && !hasOpenSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="font-display font-bold text-xl mb-2">Caja Cerrada</h2>
          <p className="text-muted-foreground mb-4">
            Debes abrir la caja antes de realizar ventas
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/pos')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <Button onClick={() => navigate('/pos/cash-session')}>
              Abrir Caja
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Products Panel */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-display font-bold text-lg">Nueva Venta</h1>
          </div>
          
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchRef}
                placeholder="Buscar o escanear código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
          </div>
          
          {/* Department tabs */}
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            <Button
              variant={selectedDept === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedDept('')}
            >
              Todos
            </Button>
            {departments.map((d) => (
              <Button
                key={d.id}
                variant={selectedDept === d.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedDept(d.id)}
              >
                {d.name}
              </Button>
            ))}
          </div>
        </header>

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              Cargando...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No hay productos disponibles
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredProducts.map((product) => (
                <motion.button
                  key={product.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => addToCart(product)}
                  className="bg-card border border-border rounded-xl p-3 text-left hover:border-primary transition-colors"
                >
                  <p className="font-medium text-sm line-clamp-2 mb-1">{product.name}</p>
                  <div className="flex items-end justify-between">
                    <p className="text-lg font-bold text-primary">${product.sale_price.toFixed(0)}</p>
                    <span className="text-xs text-muted-foreground">{product.stock} disp.</span>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cart Panel */}
      <div className="w-full md:w-96 bg-card border-t md:border-t-0 md:border-l border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Carrito
            </h2>
            {cart.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setCart([])}>
                Limpiar
              </Button>
            )}
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {cart.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Carrito vacío</p>
              <p className="text-xs">Selecciona productos para agregar</p>
            </div>
          ) : (
            cart.map((item) => (
              <motion.div
                key={item.product_id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-secondary rounded-lg p-3"
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="font-medium text-sm flex-1 pr-2">{item.product_name}</p>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive"
                    onClick={() => removeFromCart(item.product_id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.product_id, -1)}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => updateQuantity(item.product_id, 1)}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="font-bold">${item.subtotal.toFixed(2)}</p>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Cart Totals & Checkout */}
        <div className="p-4 border-t border-border bg-background space-y-3">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>${totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">IVA (16%)</span>
              <span>${totals.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
              <span>Total</span>
              <span className="text-primary">${totals.total.toFixed(2)}</span>
            </div>
          </div>
          
          <Button
            className="w-full h-12 text-lg"
            disabled={cart.length === 0}
            onClick={() => setShowPayment(true)}
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Cobrar ${totals.total.toFixed(2)}
          </Button>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showPayment && (
          <PaymentModal
            cart={cart}
            totals={totals}
            onClose={() => setShowPayment(false)}
            onComplete={handleCompleteSale}
          />
        )}
        {completedSale && (
          <SaleCompleteModal
            sale={completedSale}
            onClose={() => setCompletedSale(null)}
            onNewSale={() => { setCompletedSale(null); searchRef.current?.focus(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
